/**
 * PhD Competence Model - Competency Data Definitions
 *
 * Based on the PhD Competence Model developed by Empower your PhD project team (SoFoKleS)
 * 6 competency groups with 26 competencies total
 */

export const competencyData = {
  'Research skills and knowledge': [
    {
      name: 'Research knowledge',
      items: [
        'I have detailed knowledge of my research field and broad awareness of related research fields.'
      ]
    },
    {
      name: 'Research skills',
      items: [
        'I can define scientific questions and hypotheses.',
        'I can design solid experiments and use appropriate data collection and analysis methods.',
        'I can interpret and critically analyse experimental results and scientific literature.'
      ]
    },
    {
      name: 'Academic writing',
      items: [
        'I can write scientific manuscripts with appropriate use of grammar, phrasing, wording and structure.'
      ]
    }
  ],
  'Personal effectiveness': [
    {
      name: 'Resilience',
      items: [
        'I can work effectively and focused, despite adversities such as stress, lack of results, opposition or distractions.'
      ]
    },
    {
      name: 'Self-awareness',
      items: [
        'I can critically analyse my own behaviour and attitude, and I am open to evaluation by others. I can learn from these evaluations and I welcome feedback.'
      ]
    },
    {
      name: 'Work-life balance',
      items: [
        'I can effectively balance work and private life in a way that gives me satisfaction and peace of mind.'
      ]
    },
    {
      name: 'Inquisitiveness',
      items: ['I have a curiosity-driven mindset and can apply this to my research.']
    },
    {
      name: 'Initiative',
      items: [
        'I can anticipate and act on opportunities (active behaviour) instead of waiting for others (passive behaviour).'
      ]
    }
  ],
  'Professional development': [
    {
      name: 'Adaptivity',
      items: [
        'I can adjust my behaviour, approach and planning to changing circumstances, unexpected results and new insights.'
      ]
    },
    {
      name: 'Ambition',
      items: [
        'I have the will to learn, progress and push the boundaries of my own capabilities. I am eager to obtain more responsibilities.'
      ]
    },
    {
      name: 'Creativity',
      items: [
        'I can develop new ideas or applications and create innovative solutions to problems.'
      ]
    },
    {
      name: 'Entrepreneurship',
      items: [
        'I seek opportunities and possibilities for the development of (commercial) ideas, act accordingly and I am willing to take well-considered risks.'
      ]
    }
  ],
  'Leadership & Management': [
    {
      name: 'Collaboration',
      items: [
        'I contribute to a joint result, even when the topic is not directly related to my own task.'
      ]
    },
    {
      name: 'Coaching',
      items: [
        'I enable co-workers to grow and succeed through feedback, instruction, and encouragement.'
      ]
    },
    {
      name: 'Delegation',
      items: [
        'I can assign tasks or responsibilities in an effective manner to an appropriate colleague/student.'
      ]
    },
    {
      name: 'Decisiveness',
      items: [
        'I take responsibility for and am committed to making appropriate decisions in a timely manner.'
      ]
    },
    {
      name: 'Negotiation',
      items: [
        'I effectively communicate views and arguments to identify common goals leading to agreement of all involved parties.'
      ]
    },
    {
      name: 'Vision',
      items: [
        'I have the ability to place results and project in a bigger picture to formulate broad long-term goals.'
      ]
    }
  ],
  Communication: [
    {
      name: 'Team communication',
      items: [
        'I can engage in effective face-to-face communication with one or more people from diverse backgrounds and can listen with an open mind.'
      ]
    },
    {
      name: 'Networking',
      items: [
        'I can develop and maintain a network of contacts within and outside the organization. I can use my network to obtain or access knowledge to further common goals.'
      ]
    },
    {
      name: 'Public communication',
      items: [
        'I can actively reach out to a broad audience, such as non-scientists, to communicate my research.'
      ]
    },
    {
      name: 'Academic communication',
      items: ['I can communicate my message clearly and effectively to academic audiences.']
    }
  ],
  Teaching: [
    {
      name: 'Learning and teaching skills',
      items: [
        'I can establish and communicate learning goals to students. I can collect and use various sources of information, including student feedback, to improve learning experience.'
      ]
    },
    {
      name: 'Course designing skills',
      items: [
        "I demonstrate knowledge of subject matter, design suitable learning modules based on students' background and make effective use of materials and technology in teaching practice."
      ]
    },
    {
      name: 'Supervising skills',
      items: [
        'I can supervise individual students and groups of students with writing bachelor and master theses and in performing work for research internships.'
      ]
    },
    {
      name: 'Professional educator skills',
      items: [
        'I can reflect on teaching practice, engage and support students, and develop as a professional educator.'
      ]
    }
  ]
};

export const groupOrder = [
  'Research skills and knowledge',
  'Personal effectiveness',
  'Professional development',
  'Leadership & Management',
  'Communication',
  'Teaching'
];

export const yearLabels = ['#1', '#2', '#3', '#4', '#5', '#6', '#7', '#8'];

// Color scheme for charts - AcademicTransfer brand colors
export const chartColors = [
  { border: 'rgba(0, 75, 104, 1)', bg: 'rgba(0, 75, 104, 0.2)' }, // AT Primary Teal #004b68
  { border: 'rgba(0, 61, 84, 1)', bg: 'rgba(0, 61, 84, 0.2)' }, // Dark Teal #003d54
  { border: 'rgba(79, 213, 178, 1)', bg: 'rgba(79, 213, 178, 0.2)' }, // Mint #4fd5b2
  { border: 'rgba(238, 162, 76, 1)', bg: 'rgba(238, 162, 76, 0.2)' }, // Orange #eea24c
  { border: 'rgba(64, 188, 214, 1)', bg: 'rgba(64, 188, 214, 0.2)' }, // Teal Light #40bcd6
  { border: 'rgba(232, 229, 116, 1)', bg: 'rgba(232, 229, 116, 0.2)' }, // Yellow #e8e574
  { border: 'rgba(169, 191, 127, 1)', bg: 'rgba(169, 191, 127, 0.2)' }, // Green #a9bf7f
  { border: 'rgba(0, 51, 72, 1)', bg: 'rgba(0, 51, 72, 0.2)' } // Dark #003348
];

export function getColorForIndex(index, alpha = 1) {
  const colors = chartColors;
  const color = colors[index % colors.length];
  if (alpha !== 1) {
    return color.bg.replace('0.2', String(alpha));
  }
  return color.border;
}
